import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { trackEvent } from '../utils/mixpanel'

/**
 * Generic Email OTP Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {function} props.onClose - Callback when modal is closed
 * @param {string} props.title - Modal title
 * @param {string} props.emoji - Emoji icon displayed at top
 * @param {string} props.emailDescription - Description text for email step
 * @param {string} props.otpDescription - Description text for OTP step
 * @param {string} props.emailHint - Hint text below submit button on email step
 * @param {function} props.requestOtp - Async function(email) => { success, userExists?, code? }
 * @param {function} props.verifyOtp - Async function(email, code) => response data
 * @param {function} props.onSuccess - Callback with response data after successful verification
 * @param {function} props.onUserNotFound - Optional callback(email) when user doesn't exist
 * @param {boolean} props.allowNewUserRegistration - Enable registration form for new users (default: false)
 * @param {function} props.registerNewUser - Async function(email, name, phone) => response data (required if allowNewUserRegistration is true)
 * @param {string} props.registerDescription - Description text for registration step
 * @param {string} props.initialEmail - Optional pre-filled email (skips email step, auto-requests OTP)
 */
const EmailOtpModal = ({
  isOpen,
  onClose,
  title = 'Login',
  emoji = '🔐',
  emailDescription = 'Enter your email to receive a verification code',
  otpDescription = 'Enter the 4-digit code sent to your email',
  emailHint = "We'll send a verification code to your email.",
  requestOtp,
  verifyOtp,
  onSuccess,
  onUserNotFound,
  allowNewUserRegistration = false,
  registerNewUser,
  registerDescription = 'Complete your registration to continue',
  initialEmail = '',
}) => {
  const [email, setEmail] = useState(initialEmail)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState(initialEmail ? 'loading' : 'email') // 'email', 'otp', 'register', 'loading'
  const [otp, setOtp] = useState(['', '', '', ''])
  const [resendCooldown, setResendCooldown] = useState(0)
  const [devCode, setDevCode] = useState('')
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)]
  const hasAutoRequestedOtp = useRef(false)
  
  // Registration form state
  const [registerData, setRegisterData] = useState({ name: '', phone: '' })

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Reset to loading step when modal opens with initialEmail
  useEffect(() => {
    if (isOpen && initialEmail) {
      setEmail(initialEmail)
      setStep('loading')
      setOtp(['', '', '', ''])
      setError('')
      setDevCode('')
      hasAutoRequestedOtp.current = false
    }
  }, [isOpen, initialEmail])

  // Auto-request OTP when initialEmail is provided and modal opens
  useEffect(() => {
    const autoRequestOtp = async () => {
      if (isOpen && initialEmail && step === 'loading' && !hasAutoRequestedOtp.current) {
        hasAutoRequestedOtp.current = true
        try {
          const result = await requestOtp(initialEmail)
          if (result.success) {
            // Store dev code if available (for development)
            // Handle both { code } and { data: { code } } formats
            const devCodeValue = result.code || result.data?.code
            if (devCodeValue) {
              setDevCode(devCodeValue)
            }
            setStep('otp')
            setResendCooldown(30)
          } else {
            setError(result.message || 'Failed to send verification code')
            setStep('email')
          }
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to send verification code')
          setStep('email')
        }
      }
    }
    
    autoRequestOtp()
  }, [isOpen, initialEmail, step, requestOtp])

  // Reset hasAutoRequestedOtp when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasAutoRequestedOtp.current = false
    }
  }, [isOpen])

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)

    try {
      const result = await requestOtp(email.trim())

      if (result.success) {
        // Check if user exists (for flows that check this)
        if (result.userExists === false && onUserNotFound) {
          handleClose()
          onUserNotFound(email.trim())
          return
        }

        // User exists or no user check - show OTP input
        setStep('otp')
        setResendCooldown(30)
        // Handle both { code } and { data: { code } } formats
        const devCodeValue = result.code || result.data?.code
        if (devCodeValue) {
          setDevCode(devCodeValue)
        }
      }
    } catch (err) {
      console.error('Error requesting OTP:', err)
      setError(err.response?.data?.message || 'An error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    setError('')

    // Auto-advance to next input
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus()
    }

    // Auto-submit when all digits are entered
    if (value && index === 3 && newOtp.every(d => d !== '')) {
      handleOtpSubmit(newOtp.join(''))
    }
  }

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus()
    }
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 4)
    if (/^\d{1,4}$/.test(pastedData)) {
      const digits = pastedData.split('')
      const newOtp = [...otp]
      digits.forEach((digit, i) => {
        if (i < 4) newOtp[i] = digit
      })
      setOtp(newOtp)
      
      // Focus on the next empty input or last input
      const nextEmptyIndex = newOtp.findIndex(d => d === '')
      if (nextEmptyIndex !== -1) {
        otpRefs[nextEmptyIndex].current?.focus()
      } else {
        otpRefs[3].current?.focus()
        // Auto-submit if all filled
        handleOtpSubmit(newOtp.join(''))
      }
    }
  }

  const handleOtpSubmit = async (code = otp.join('')) => {
    if (code.length !== 4) {
      setError('Please enter the complete 4-digit code')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const result = await verifyOtp(email.trim(), code)

      if (result.success) {
        // Check if user needs to complete registration
        if (result.data.needsRegistration && allowNewUserRegistration) {
          setStep('register')
        } else if (result.data.needsRegistration && !allowNewUserRegistration && onUserNotFound) {
          // Fallback to onUserNotFound if registration not allowed
          handleClose()
          onUserNotFound(email.trim())
        } else {
          if (onSuccess) {
            onSuccess(result.data)
          }
          handleClose()
        }
      }
    } catch (err) {
      console.error('Error verifying OTP:', err)
      setError(err.response?.data?.message || 'Invalid or expired code. Please try again.')
      setOtp(['', '', '', ''])
      otpRefs[0].current?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!registerData.name.trim()) {
      setError('Please enter your name')
      return
    }

    setIsLoading(true)

    try {
      const result = await registerNewUser(email.trim(), registerData.name.trim(), registerData.phone.trim())

      if (result.success) {
        console.log('result', result);
        trackEvent('Sign Up', {
          signup_method: 'email',
          email: email.trim(),
        })
        if (onSuccess) {
          onSuccess(result.data)
        }
        handleClose()
      }
    } catch (err) {
      console.error('Error completing registration:', err)
      setError(err.response?.data?.message || 'Failed to complete registration. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return

    setIsLoading(true)
    setError('')
    setOtp(['', '', '', ''])

    try {
      const result = await requestOtp(email.trim())

      if (result.success) {
        setResendCooldown(30)
        // Handle both { code } and { data: { code } } formats
        const devCodeValue = result.code || result.data?.code
        if (devCodeValue) {
          setDevCode(devCodeValue)
        }
        otpRefs[0].current?.focus()
      }
    } catch (err) {
      console.error('Error resending OTP:', err)
      setError(err.response?.data?.message || 'Failed to resend code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setEmail(initialEmail || '')
    setError('')
    setStep(initialEmail ? 'loading' : 'email')
    setOtp(['', '', '', ''])
    setDevCode('')
    setResendCooldown(0)
    setRegisterData({ name: '', phone: '' })
    hasAutoRequestedOtp.current = false
    onClose()
  }

  const handleBackToEmail = () => {
    // If we have initialEmail, don't allow going back to email step
    if (initialEmail) {
      handleClose()
      return
    }
    setStep('email')
    setOtp(['', '', '', ''])
    setError('')
    setDevCode('')
    setRegisterData({ name: '', phone: '' })
  }

  const getStepDescription = () => {
    switch (step) {
      case 'email':
        return emailDescription
      case 'otp':
        return otpDescription
      case 'register':
        return registerDescription
      default:
        return ''
    }
  }

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  }

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 50 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25,
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8, 
      y: 50,
      transition: { duration: 0.2 }
    },
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={backdropVariants}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 overflow-hidden"
            variants={modalVariants}
          >
            {/* Decorative gradient */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-primary-600 via-purple-600 to-pink-500" />

            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Content */}
            <div className="text-center mb-6">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                className="text-5xl mb-4"
              >
                {step === 'register' ? '👋' : emoji}
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {step === 'register' ? 'Almost There!' : title}
              </h2>
              <p className="text-gray-600">
                {getStepDescription()}
              </p>
            </div>

            <AnimatePresence mode="wait">
              {step === 'loading' ? (
                // Loading state when auto-requesting OTP
                <motion.div
                  key="loading"
                  className="flex flex-col items-center justify-center py-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4" />
                  <p className="text-gray-600">Sending verification code...</p>
                </motion.div>
              ) : step === 'email' ? (
                // Email form
                <motion.form
                  key="email-form"
                  onSubmit={handleEmailSubmit}
                  className="space-y-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <div>
                    <label
                      htmlFor="otp-modal-email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email Address
                    </label>
                    <input
                      id="otp-modal-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setError('')
                      }}
                      placeholder="your.email@example.com"
                      className={`w-full px-4 py-3 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                        error ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
                      }`}
                      disabled={isLoading}
                      autoFocus
                    />
                    {error && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-600 text-sm mt-1"
                      >
                        {error}
                      </motion.p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-4 bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        />
                        <span>Sending code...</span>
                      </>
                    ) : (
                      'Continue'
                    )}
                  </button>

                  <p className="text-center text-sm text-gray-500 mt-4">
                    {emailHint}
                  </p>
                </motion.form>
              ) : step === 'otp' ? (
                // OTP form
                <motion.div
                  key="otp-form"
                  className="space-y-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">Code sent to:</p>
                    <p className="font-medium text-gray-900">{email}</p>
                  </div>

                  {/* OTP Input */}
                  <div className="flex justify-center gap-3">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={otpRefs[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={index === 0 ? handleOtpPaste : undefined}
                        className={`w-14 h-16 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                          error ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
                        }`}
                        disabled={isLoading}
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>

                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-600 text-sm text-center"
                    >
                      {error}
                    </motion.p>
                  )}

                  {/* Development mode: show OTP */}
                  {import.meta.env.VITE_SHOW_DEV_LINKS === 'true' && devCode && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
                      <p className="text-sm text-yellow-800">
                        Dev Mode - Code: <span className="font-bold">{devCode}</span>
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => handleOtpSubmit()}
                    disabled={isLoading || otp.some(d => d === '')}
                    className="w-full py-4 bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      'Verify & Login'
                    )}
                  </button>

                  <div className="text-center space-y-2">
                    <button
                      onClick={handleResendOtp}
                      disabled={resendCooldown > 0 || isLoading}
                      className="text-primary-600 hover:text-primary-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendCooldown > 0 
                        ? `Resend code in ${resendCooldown}s`
                        : "Didn't receive the code? Resend"
                      }
                    </button>
                    <br />
                    <button
                      onClick={handleBackToEmail}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Use a different email
                    </button>
                  </div>
                </motion.div>
              ) : (
                // Registration form (for new users after OTP verification)
                <motion.form
                  key="register-form"
                  onSubmit={handleRegisterSubmit}
                  className="space-y-4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  {/* Email verified badge */}
                  <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-xl mb-4">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Email verified: {email}</span>
                  </div>

                  <div>
                    <label
                      htmlFor="register-name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="register-name"
                      type="text"
                      value={registerData.name}
                      onChange={(e) => {
                        setRegisterData(prev => ({ ...prev, name: e.target.value }))
                        setError('')
                      }}
                      placeholder="Enter your full name"
                      className={`w-full px-4 py-3 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                        error && !registerData.name ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
                      }`}
                      disabled={isLoading}
                      autoFocus
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="register-phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="register-phone"
                      type="tel"
                      value={registerData.phone}
                      onChange={(e) => {
                        setRegisterData(prev => ({ ...prev, phone: e.target.value }))
                      }}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all border-gray-200 bg-gray-50"
                      disabled={isLoading}
                    />
                  </div>

                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-600 text-sm"
                    >
                      {error}
                    </motion.p>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !registerData.name.trim()}
                    className="w-full py-4 bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        />
                        <span>Completing registration...</span>
                      </>
                    ) : (
                      'Complete Registration'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    className="w-full text-gray-500 hover:text-gray-700 text-sm py-2"
                  >
                    Use a different email
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default EmailOtpModal
